// useShowPopup.js
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

const TITLE_KEY = {
    success: 'popup.default.success.title',
    error:   'popup.default.error.title',
    warning: 'popup.default.warning.title',
    info:    'popup.default.info.title',
};
const MSG_KEY = {
    success: 'popup.default.success.message',
    error:   'popup.default.error.message',
    warning: 'popup.default.warning.message',
    info:    'popup.default.info.message',
};

export default function useShowPopup() {
    const { t } = useTranslation();

    const showPopup = (typeOrCfg, message, overrides) => {
        const cfg = typeof typeOrCfg === 'string'
            ? { type: typeOrCfg, message, ...(overrides || {}) }
            : (typeOrCfg || {});

        const {
            type = 'info',
            title,
            message: text,
            titleKey,
            messageKey,
            html,
            showCancel = false,
            confirmText,
            cancelText,
            // ✅ keep your defaults, allow overrides
            confirmButtonColor = '#0A564B',
            cancelButtonColor  = '#999',
            ...rest
        } = cfg;

        const resolvedTitle = titleKey ? t(titleKey) : (title ?? t(TITLE_KEY[type]));
        const resolvedText  = html !== undefined
            ? undefined
            : (messageKey ? t(messageKey) : (text ?? t(MSG_KEY[type])));

        return Swal.fire({
            icon: type,
            title: resolvedTitle,
            html,
            text: resolvedText,
            showCancelButton: showCancel,
            confirmButtonText: confirmText ?? t('popup.button.ok'),
            cancelButtonText:  showCancel ? (cancelText ?? t('popup.button.cancel')) : undefined,
            confirmButtonColor,
            cancelButtonColor: showCancel ? cancelButtonColor : undefined,
            ...rest,
        });
    };

    // keep your sugar helpers
    showPopup.success = (message, options = {}) =>
        showPopup({ type: 'success', message, ...options });

    showPopup.error = (message, options = {}) =>
        showPopup({ type: 'error', message, ...options });

    showPopup.warning = (message, options = {}) =>
        showPopup({ type: 'warning', message, ...options });

    showPopup.info = (message, options = {}) =>
        showPopup({ type: 'info', message, ...options });

    // optional enum on the function
    showPopup.Type = Object.freeze({
        SUCCESS: 'success',
        ERROR: 'error',
        WARNING: 'warning',
        INFO: 'info',
    });

    return showPopup;
}

// Usage examples
// showPopup.error("message")
// showPopup.warning
// showPopup('warning', null, {
//     showCancel: true,
//     // confirm/cancel texts default to i18n: popup.button.ok / popup.button.cancel
//     // confirmButtonColor '#0A564B', cancelButtonColor '#999' are defaulted too
// });
